import React, { useCallback, useEffect, useState } from 'react';
import { DocumentScanning } from 'tradera/components/international-identification-modal/document-scanning';
import { useTranslator } from 'tradera/lang/translate';
import { memberIdentificationClient } from 'tradera/utils/http';
import { LocalLoader } from 'tradera/components/loader/loader';
import { MitId } from 'tradera/components/international-identification-modal/mit-id';
import { MitIdLogo } from './mit-id-logo';
import styles from './international-identification-modal.module.scss';
import { CardVerification } from './card-verification';
import { logger } from 'packages/logger';
import { Button } from '../atoms/button/button';
import { ScrollableModal } from '../scrollable-modal/scrollable-modal';
import { SignicatScanning } from 'tradera/components/international-identification-modal/signicat-scanning';

const IDENTIFICATION_TYPES = {
    MITID: 'MitId',
    CARD: 'Card',
    DOCUMENTSCANNING: 'DocumentScanning',
    SIGNICATSCANNING: 'SignicatScanning'
};

export const InternationalIdentificationModal = ({
    heading,
    introText,
    onCloseModal,
    onVerifyComplete,
    purpose
}) => {
    const { t } = useTranslator();

    const [validIdentifications, setValidIdentifications] = useState(null);
    const [error, setError] = useState(null);
    const [isHidden, setIsHidden] = useState(false);
    const [selectedIdentification, setSelectedIdentification] = useState(null);

    useEffect(() => {
        memberIdentificationClient
            .query(`getvalididentifications?purpose=${purpose}`)
            .then((data) => {
                setValidIdentifications(data.validIdentifications);
                return;
            })
            .catch(setError);
    }, [setValidIdentifications, purpose]);

    const onCancel = useCallback(() => setSelectedIdentification(null), []);

    const selectedIdentificationGui = {
        [IDENTIFICATION_TYPES.CARD]: () => (
            <CardVerification
                onCardVerified={onVerifyComplete}
                setIsHidden={setIsHidden}
            />
        ),
        [IDENTIFICATION_TYPES.DOCUMENTSCANNING]: () => (
            <DocumentScanning
                onVerifyComplete={onVerifyComplete}
                purpose={purpose}
            />
        ),
        [IDENTIFICATION_TYPES.SIGNICATSCANNING]: () => (
            <SignicatScanning
                onVerifyComplete={onVerifyComplete}
                purpose={purpose}
            />
        ),
        [IDENTIFICATION_TYPES.MITID]: () => (
            <MitId
                onVerifyComplete={onVerifyComplete}
                onVerifyCancel={onCancel}
                purpose={purpose}
            />
        )
    }[selectedIdentification];

    return (
        <ScrollableModal
            show={!isHidden}
            title={heading}
            onClose={onCloseModal}>
            {!selectedIdentificationGui && validIdentifications && (
                <div className="d-flex flex-column px-7 py-4 text-center">
                    <div className="mb-4">{introText}</div>
                    {validIdentifications.map((identification, index) => {
                        let element = null;
                        switch (identification) {
                            case IDENTIFICATION_TYPES.MITID:
                                element = (
                                    <Button
                                        theme="primary"
                                        fluid
                                        className={`d-flex justify-content-center align-items-center ${styles.internationalIdentificationModalMitIdButton}`}
                                        onClick={() =>
                                            setSelectedIdentification(
                                                IDENTIFICATION_TYPES.MITID
                                            )
                                        }>
                                        <MitIdLogo
                                            className="mr-2"
                                            color="white"
                                        />
                                        {t(
                                            'international_identification_mitid_button'
                                        )}
                                    </Button>
                                );
                                break;
                            case IDENTIFICATION_TYPES.DOCUMENTSCANNING:
                                element = (
                                    <Button
                                        theme="action"
                                        variant="outline"
                                        fluid
                                        onClick={() =>
                                            setSelectedIdentification(
                                                IDENTIFICATION_TYPES.DOCUMENTSCANNING
                                            )
                                        }>
                                        {t(
                                            'international_identification_document_scanning_button'
                                        )}
                                    </Button>
                                );
                                break;
                            case IDENTIFICATION_TYPES.SIGNICATSCANNING:
                                element = (
                                    <Button
                                        theme="action"
                                        variant="outline"
                                        fluid
                                        onClick={() =>
                                            setSelectedIdentification(
                                                IDENTIFICATION_TYPES.SIGNICATSCANNING
                                            )
                                        }>
                                        {t(
                                            'international_identification_signicat_scanning_button'
                                        )}
                                    </Button>
                                );
                                break;
                            case IDENTIFICATION_TYPES.CARD:
                                element = (
                                    <Button
                                        theme="action"
                                        variant="outline"
                                        fluid
                                        onClick={() =>
                                            setSelectedIdentification(
                                                IDENTIFICATION_TYPES.CARD
                                            )
                                        }>
                                        {t(
                                            'international_identification_card_verification_button'
                                        )}
                                    </Button>
                                );
                                break;
                            default:
                                logger(
                                    `Invalid identification method: ${identification}`
                                );
                        }
                        return (
                            <React.Fragment key={index}>
                                {index > 0 && (
                                    <div className="position-relative w-100 my-2">
                                        <span
                                            className={
                                                styles.internationalIdentificationModalDelineator
                                            }>
                                            {t(
                                                'international_identification_or'
                                            )}
                                        </span>
                                        <hr />
                                    </div>
                                )}
                                {element}
                            </React.Fragment>
                        );
                    })}
                </div>
            )}
            {selectedIdentificationGui && selectedIdentificationGui()}
            {!validIdentifications && !error && <LocalLoader />}
            {error && (
                <div className="px-7 py-4 text-center text-danger">
                    {t('international_identification_errorUnavailable')}
                </div>
            )}
        </ScrollableModal>
    );
};
